import { useState, useEffect, ChangeEvent, KeyboardEvent, useMemo } from 'react'
import { OrderItem, RedeemCode } from 'mytours-read-api-typescript'
import { LockAppContainer as Container, getDirectApi } from 'mytours-read-common'
import { POLL_ATTEMPTS } from '../useBundlePurchaseReturn'
import { ionHistory } from 'mobile_web/modules/navigation'
import PayModal from './PayModal'
import SuccessModal from './SuccessModal'
import { formatCurrency } from '../functions'
import Page from '../../common/components/Page'
import { Button, Card, Spinner, Input, Text } from '@mytours/components'
import { ImageSizePreset } from '../../../common_lib/types'
import { generateImageUrl } from '../../../common_lib/utils'
import strings from '../../../strings'

import classnames from 'classnames'
import '../styles.less'

interface ImageDimension {
	width: number
	height: number
}

// The lock screen only redeems collection and whole-app (project) codes; map those onto the order
// item type the pay modal needs. Explicit rather than a cast so an unhandled redeem type can't slip
// an invalid value into a purchase.
const toOrderItemType = (linkedType: RedeemCode.LinkedTypeEnum): OrderItem.ItemTypeEnum => {
	switch (linkedType) {
		case RedeemCode.LinkedTypeEnum.Project:
			return OrderItem.ItemTypeEnum.Project
		case RedeemCode.LinkedTypeEnum.Collection:
		default:
			return OrderItem.ItemTypeEnum.Collection
	}
}

// Which order item type this gate owns, for deciding whether a Stripe return is ours. Deliberately
// exhaustive and undefined for anything else, so the test fails closed: toOrderItemType folds Bundle
// and a missing linkedType onto Collection, which is right when buying and wrong when claiming.
const ownedOrderItemType = (linkedType?: RedeemCode.LinkedTypeEnum): OrderItem.ItemTypeEnum | undefined => {
	switch (linkedType) {
		case RedeemCode.LinkedTypeEnum.Project:
			return OrderItem.ItemTypeEnum.Project
		case RedeemCode.LinkedTypeEnum.Collection:
			return OrderItem.ItemTypeEnum.Collection
		default:
			return undefined
	}
}

const LockApp = ({
	title,
	description,
	backgroundImage,
	redeemCode,
	lockApp,
	linkedId,
	linkedType,
	webPrice,
	openPayModalImmediately,
	footer,
}: Container.Props & Container.Actions) => {
	const [code, setCode] = useState<string>('')
	const [showEmailField, setShowEmailField] = useState<boolean>(false)
	const [email, setEmail] = useState<string>('')
	const [error, setError] = useState<string>('')
	const [message, setMessage] = useState<string>('')
	const [payModalOpen, setPayModalOpen] = useState<boolean>(false)
	const [successModalOpen, setSuccessModalOpen] = useState<boolean>(false)
	const [imageDimension, setImageDimension] = useState<ImageDimension>({
		width: null,
		height: null,
	})

	const handleChange =
		(prevValue: string, handler: (value: string) => void) =>
		(e: ChangeEvent<HTMLInputElement>): void => {
			const newValue = e.target.value
			// Check if the value has changed so that we don't clear out requires_email error without showing it to the user
			if (newValue !== prevValue) {
				setError('')
				return handler(newValue)
			}
		}

	const handleKeyUp = (e: KeyboardEvent<HTMLInputElement>): void => {
		if (e?.keyCode === 13) {
			onSent()
		}
	}

	useEffect(() => {
		const url = new URL(window.location.href)
		const searchParams = url.searchParams
		const orderId = Number(searchParams.get('order-id'))
		// Don't open PayModal if we are in the process of validating payment
		if (orderId) {
			return
		}

		if (webPrice && openPayModalImmediately) {
			setPayModalOpen(true)
		}
	}, [])

	useEffect(() => {
		if (linkedType === RedeemCode.LinkedTypeEnum.Project) {
			if (lockApp.app.loaded && lockApp.app.status === 'valid') {
				return
			}
		} else if (linkedType === RedeemCode.LinkedTypeEnum.Collection) {
			if (lockApp.collection.loaded && lockApp.collection.status === 'valid') {
				return
			}
		}

		const searchParams = new URL(window.location.href).searchParams
		const orderId = Number(searchParams.get('order-id'))
		const paymentIntent = searchParams.get('payment_intent')
		if (!orderId) {
			return
		}

		const owned = ownedOrderItemType(linkedType)
		let cancelled = false
		const wait = (ms: number): Promise<unknown> => new Promise(resolve => setTimeout(resolve, ms))

		const fetchOrder = async () => {
			const order = await getDirectApi().orderShow(orderId, paymentIntent)
			// Order only has one item at the moment
			return {
				itemType: order.items?.[0]?.item_type,
				code: order.items?.[0]?.code,
				email: order.email,
			}
		}

		// Drop the Stripe params so a re-render cannot reprocess the same redirect, leaving every other
		// param and the fragment alone. Passed as a string: ionHistory takes a history@4 location, and a
		// URL instance spreads to {} there, blanking search and hash.
		const stripStripeParams = (): void => {
			const params = new URL(window.location.href).searchParams
			params.delete('order-id')
			params.delete('payment_intent')
			params.delete('payment_intent_client_secret')
			params.delete('redirect_status')
			const query = params.toString()
			ionHistory.replace(window.location.pathname + (query ? '?' + query : '') + window.location.hash)
		}

		const fetchAndRedeem = async () => {
			// Establish the order is for the thing this gate sells before touching anything. A bundle
			// offered in the footer redirects back to this same page, and its code redeems against the
			// bundle, not this collection. Retried rather than given up on: until the order is readable
			// there is no way to tell whose it is, and stripping on a guess would take the params the
			// bundle's own handler needs.
			let order
			for (let attempt = 0; attempt <= POLL_ATTEMPTS && !cancelled; attempt++) {
				if (attempt > 0) {
					await wait(2000 * attempt)
					if (cancelled) {
						return
					}
				}
				try {
					order = await fetchOrder()
					break
				} catch {
					order = undefined
				}
			}
			if (cancelled || !order) {
				return
			}
			// Not ours, or a gate with no linkedType to own it with. Leave the params for whoever sold it.
			if (owned === undefined || order.itemType !== owned) {
				return
			}

			setMessage(strings.validating_payment)
			stripStripeParams()

			let { code, email } = order
			for (let retry = 1; !code && retry <= POLL_ATTEMPTS && !cancelled; retry++) {
				await wait(2000 * retry)
				if (cancelled) {
					return
				}
				try {
					const next = await fetchOrder()
					code = next.code
					email = next.email
				} catch {
					// The webhook may not have minted the code yet; keep polling.
				}
			}
			if (cancelled) {
				return
			}

			if (code) {
				setCode(code)
				setEmail(email)
				setSuccessModalOpen(true)
				return
			}

			setMessage(strings.validating_payment_failed)
		}

		void fetchAndRedeem()

		return () => {
			cancelled = true
		}
	}, [])

	const handleCloseSuccessModal = () => {
		redeemCode(code, linkedId, linkedType, email)
	}

	const { file } = backgroundImage || {}

	useEffect(() => {
		if (linkedType === RedeemCode.LinkedTypeEnum.Collection && linkedId !== lockApp.linkedId) {
			return
		}

		if (linkedType === RedeemCode.LinkedTypeEnum.Project) {
			if (!lockApp.app.loading && lockApp.app.loaded && lockApp.app.status !== 'valid') {
				if (lockApp.app.status === 'expired') {
					setError(strings.code_expired)
				} else if (lockApp.app.status === 'max_redemptions') {
					setError(strings.code_max_redemptions)
				} else if (lockApp.app.status === 'requires_email') {
					setError(email ? strings.code_email_mismatch : '')
					setCode(lockApp.couponCode)
					setShowEmailField(true)
				} else {
					setError(strings.code_invalid)
				}
			}
		} else if (linkedType === RedeemCode.LinkedTypeEnum.Collection) {
			if (!lockApp.collection.loading && lockApp.collection.loaded && lockApp.collection.status !== 'valid') {
				if (lockApp.collection.status === 'expired') {
					setError(strings.code_expired)
				} else if (lockApp.collection.status === 'max_redemptions') {
					setError(strings.code_max_redemptions)
				} else if (lockApp.collection.status === 'requires_email') {
					setError(email ? strings.code_email_mismatch : '')
					setCode(lockApp.couponCode)
					setShowEmailField(true)
				} else {
					setError(strings.code_invalid)
				}
			}
		}
	}, [lockApp])

	useEffect(() => {
		setupImageDimension()
		addEventListener('resize', setupImageDimension)

		return () => {
			removeEventListener('resize', setupImageDimension)
		}
	}, [imageDimension])

	const setupImageDimension = (): void => {
		const width: number = window.innerWidth
		const height: number = window.innerHeight

		if (imageDimension.width !== width || imageDimension.height !== height) {
			setImageDimension({ width, height })
		}
	}

	const loading =
		(linkedType === RedeemCode.LinkedTypeEnum.Collection && lockApp.collection.loading) ||
		(linkedType === RedeemCode.LinkedTypeEnum.Project && lockApp.app.loading)

	const onSent = (): void => {
		if (loading) {
			return
		}

		redeemCode(code, linkedId, linkedType, email)
	}

	const titleNode = useMemo(() => {
		const defaultTitle =
			(linkedType === RedeemCode.LinkedTypeEnum.Collection && strings.unlock_the_collection) ||
			(linkedType === RedeemCode.LinkedTypeEnum.Project && strings.unlock_the_app)
		return (title || defaultTitle) && <h1>{title || defaultTitle}</h1>
	}, [title])

	const bgImagePath =
		file?.original_url &&
		`${generateImageUrl(file, {
			imageSizePreset: ImageSizePreset.Fullscreen,
			blur: 10,
		})}`

	const price = webPrice ? formatCurrency(webPrice.currency, webPrice.amount) : ''

	const togglePayModal = (): void => {
		setPayModalOpen(!payModalOpen)
	}

	const payModalLinkedType = toOrderItemType(linkedType)
	const payModalLinkedId = linkedId

	return (
		<Page
			contentProps={{
				className: classnames('lock-app'),
				style: {
					background: `url(${bgImagePath}) no-repeat center center / cover`,
					'--content-background-image': 'url()',
				},
			}}
			dataCy="page-lock-app"
		>
			<Card variant="ios" className="center-block">
				<i className="lock-icon fal lock-alt" />
				{titleNode}
				<p dangerouslySetInnerHTML={{ __html: description || strings.enter_your_access_code_to_continue }} />
				<Input
					variant="ios"
					value={code}
					onChange={handleChange(code, setCode)}
					onKeyUp={handleKeyUp}
					aria-label={strings.enter_your_access_code_to_continue}
				/>
				{showEmailField && <p>{strings.code_enter_purchase_email}</p>}
				{showEmailField && (
					<Input
						variant="ios"
						value={email}
						onChange={handleChange(email, setEmail)}
						onKeyUp={handleKeyUp}
						placeholder={`${strings.email}:`}
					/>
				)}
				{error && <div className="error">{error}</div>}
				{message && <div>{message}</div>}

				<Button variant="primary" className="lock-button" aria-label={strings.unlock} onClick={onSent}>
					<Text variant="inherit">{strings.unlock}</Text>
					{loading && <Spinner variant="crescent" aria-label={strings.loading} />}
				</Button>

				{webPrice ? (
					<>
						<br />
						<Button
							variant="primary"
							className="buy-button"
							aria-label={strings.formatString(strings.buy_for, { price }) as string}
							onClick={togglePayModal}
						>
							<Text variant="inherit">{strings.formatString(strings.buy_for, { price })}</Text>
							{loading && <Spinner variant="crescent" aria-label={strings.loading} />}
						</Button>
					</>
				) : null}
				{footer}

				{payModalOpen && webPrice && (
					<PayModal
						titleNode={titleNode}
						webPrice={webPrice}
						itemId={payModalLinkedId}
						itemType={payModalLinkedType}
						onClose={togglePayModal}
					/>
				)}
				{successModalOpen && <SuccessModal onCloseSuccessModal={handleCloseSuccessModal} />}
			</Card>
		</Page>
	)
}

const LockAppComponent = Container.default(LockApp)
export default LockAppComponent
